'use client';
import { useState } from 'react';

export type ColorTheme = 'classic'|'ocean'|'fire'|'neon'|'aurora'|'mono'|'iridescence';
export interface GlobeSettings {
  showWind:        boolean;
  timeScale:       number;
  particleCount:   number;
  trailFade:       number;
  trailWidth:      number;
  particleOpacity: number;
  globeBrightness: number;
  reverseFlow:     boolean;
  animMode:        'wind' | 'straight' | 'random';
  colorTheme:      ColorTheme;
  planetSurface:   string;
  textureQuality:  '2k' | '8k';
  bgScene:         'stars' | 'milkyway' | 'heart' | 'heartsoul';
  glowHue:         number;
  glowOpacity:     number;
}
export const DEFAULT_SETTINGS: GlobeSettings = {
  showWind:        true,
  timeScale:       1800,
  particleCount:   45000,
  trailFade:       0.025,
  trailWidth:      0.9,
  particleOpacity: 0.72,
  globeBrightness: 0.85,
  reverseFlow:     false,
  animMode:        'wind',
  colorTheme:      'classic',
  planetSurface:   'earth',
  textureQuality:  '2k',
  bgScene:         'stars',
  glowHue:         0,
  glowOpacity:     1.0,
};
const PLANET_THEMES: Partial<Record<string, ColorTheme>> = {
  earth:    'classic',
  '2322':   'aurora',
  oceanless:'classic',
  moon:     'mono',
  tethys:   'mono',
  mars:     'fire',
  venus:    'neon',
  jupiter:  'aurora',
  neptune:  'ocean',
  lava:     'fire',
  sun:      'fire',
  cares:    'mono',
};
const SURFACE_CATEGORIES = [
  {
    id: 'earth', label: 'Earth',
    planets: [
      { id: 'earth',     label: 'Earth',     dot: '#1e6abb' },
      { id: '2322',      label: '2322',       dot: '#3a8a6a' },
      { id: 'oceanless', label: 'Oceanless',  dot: '#8a6a3a' },
    ],
  },
  {
    id: 'moon', label: 'Moon',
    planets: [
      { id: 'moon',   label: 'Moon',   dot: '#8a8a8a' },
      { id: 'tethys', label: 'Tethys', dot: '#b8c8d8' },
    ],
  },
  {
    id: 'planets', label: 'Planets',
    planets: [
      { id: 'mars',    label: 'Mars',    dot: '#8b3a1a' },
      { id: 'venus',   label: 'Venus',   dot: '#c8901a' },
      { id: 'jupiter', label: 'Jupiter', dot: '#c8a060' },
      { id: 'neptune', label: 'Neptune', dot: '#2a5aa0' },
    ],
  },
  {
    id: 'dwarf', label: 'Dwarf',
    planets: [
      { id: 'sun',   label: 'Sun',   dot: '#ff9500' },
      { id: 'cares', label: 'Cares', dot: '#9a9a8a' },
    ],
  },
  {
    id: 'scifi', label: 'Sci-Fi',
    planets: [
      { id: 'lava', label: 'Lava', dot: '#ff3d00' },
    ],
  },
] as const;
const THEMES = [
  { id:'classic', label:'Classic', preview:['#116ebb','#2cb7a8','#4cc47b','#aad539','#fdd62b','#f07c1a','#d43715'] },
  { id:'ocean',   label:'Ocean',   preview:['#001440','#003c78','#006eb4','#00aadc','#32d2f0','#96ebff'] },
  { id:'fire',    label:'Fire',    preview:['#0a001e','#3c0000','#961400','#d24600','#fa8c00','#ffd200'] },
  { id:'neon',    label:'Neon',    preview:['#000032','#1e0078','#0032dc','#00c8ff','#00ff64','#ff0096'] },
  { id:'aurora',  label:'Aurora',  preview:['#050a1e','#003c50','#007864','#00c896','#b4a0ff','#dc64ff'] },
  { id:'mono',    label:'Mono',    preview:['#141416','#383b47','#6e7380','#adb2bf','#d5dae6','#ffffff'] },
  { id:'iridescence', label:'Iridescence', preview:['#b400ff','#0064ff','#00dcff','#00ff78','#b4ff00','#ffd200','#ff0096'] },
] as const;
function LogoMark({ size = 32 }: { size?: number }) {
  const s = size / 40;
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="23" r="12"
        stroke="rgba(255,255,255,0.88)" strokeWidth={1.8 / s} fill="none"/>
      <ellipse cx="20" cy="23" rx="12" ry="3"
        stroke="rgba(255,255,255,0.16)" strokeWidth={0.7 / s} fill="none"/>
      <path d="M 5.5 16 A 16 16 0 0 1 34.5 16"
        stroke="#00e5cc" strokeWidth={1.8 / s} strokeLinecap="round" fill="none"/>
    </svg>
  );
}

function Slider({ label, value, min, max, step, display, onChange, accent = '#00e5cc' }: {
  label: string; value: number; min: number; max: number; step: number;
  display: string; onChange: (v: number) => void; accent?: string;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize:'10px', fontWeight:500, letterSpacing:'0.08em',
          color:'rgba(255,255,255,0.45)', textTransform:'uppercase' }}>
          {label}
        </span>
        <span style={{ fontSize:'10px', fontWeight:600, color:accent,
          minWidth:'36px', textAlign:'right' }}>
          {display}
        </span>
      </div>
      <div style={{ position:'relative', height:'20px' }}>
        <div style={{ position:'absolute', left:0, height:'2px', width:'100%',
          background:'rgba(255,255,255,0.08)', borderRadius:'1px', top:'9px' }}/>
        <div style={{ position:'absolute', left:0, height:'2px', width:pct+'%',
          background:accent, borderRadius:'1px', opacity:0.6, top:'9px' }}/>
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ position:'absolute', width:'100%', height:'20px',
            opacity:0, cursor:'pointer', margin:0, padding:0, zIndex:2, top:0 }}/>
        <div style={{ position:'absolute', left:'calc('+pct+'% - 7px)',
          top:'3px', width:'14px', height:'14px', borderRadius:'50%', background:accent,
          boxShadow:'0 0 8px '+accent+'88', pointerEvents:'none' }}/>
      </div>
    </div>
  );
}

function Section({ title }: { title: string }) {
  return (
    <div style={{ fontSize:'9px', fontWeight:600, letterSpacing:'0.22em',
      color:'rgba(255,255,255,0.2)', textTransform:'uppercase',
      margin:'20px 0 14px' }}>
      {title}
    </div>
  );
}

export default function ControlPanel({
  onChange,
}: {
  onChange: (p: Partial<GlobeSettings>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [s, setS]       = useState<GlobeSettings>(DEFAULT_SETTINGS);

  function upd<K extends keyof GlobeSettings>(key: K, val: GlobeSettings[K]) {
    const next = { ...s, [key]: val };
    setS(next);
    onChange({ [key]: val });
  }

  const trailLabel = s.trailFade<=0.012?'Very long'
    : s.trailFade<=0.022?'Long'
    : s.trailFade<=0.038?'Medium'
    : s.trailFade<=0.06?'Short':'Minimal';

  return (
    <>
      {/* ── FLOATING BUTTON ────────────────────────────── */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open Atmosphere8K controls"
        style={{
          position:        'fixed',
          top:             '16px',
          left:            '16px',
          zIndex:          1001,
          display:         'flex',
          alignItems:      'center',
          gap:             '10px',
          background:      'rgba(0,6,14,0.82)',
          backdropFilter:  'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border:          '1px solid rgba(0,229,204,0.18)',
          borderRadius:    '12px',
          padding:         '10px 14px 10px 10px',
          cursor:          'pointer',
          opacity:         open ? 0 : 1,
          transform:       open ? 'translateX(-140px)' : 'translateX(0)',
          transition:      'opacity 0.3s ease, transform 0.35s cubic-bezier(.4,0,.2,1)',
          pointerEvents:   open ? 'none' : 'auto',
          fontFamily:      "'Inter', system-ui, sans-serif",
        }}
      >
        <LogoMark size={28} />
        <div style={{ display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', alignItems:'baseline', gap:'2px' }}>
            <span style={{ fontSize:'11px', fontWeight:300, letterSpacing:'0.1em',
              color:'rgba(255,255,255,0.9)' }}>ATMOSPHERE</span>
            <span style={{ fontSize:'11px', fontWeight:700, letterSpacing:'0.04em',
              color:'#00e5cc' }}>8K</span>
          </div>
          <div style={{ fontSize:'9px', color:'rgba(255,255,255,0.28)',
            letterSpacing:'0.08em', marginTop:'1px' }}>
            {s.showWind ? 'Wind active' : 'Wind off'}
          </div>
        </div>
      </button>

      {/* ── BACKDROP (mobile tap to close) ─────────────── */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position:   'fixed', inset:0,
            background: 'rgba(0,0,0,0.25)',
            zIndex:     999,
            backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* ── SLIDE PANEL ────────────────────────────────── */}
      <div
        role="dialog"
        aria-label="Atmosphere8K Controls"
        style={{
          position:        'fixed',
          top:             0,
          left:            0,
          bottom:          0,
          width:           'min(300px, 85vw)',
          transform:       open ? 'translateX(0)' : 'translateX(-100%)',
          transition:      'transform 0.35s cubic-bezier(.4,0,.2,1)',
          background:      'rgba(4,10,28,0.96)',
          backdropFilter:  'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRight:     '1px solid rgba(0,229,204,0.06)',
          zIndex:          1000,
          display:         'flex',
          flexDirection:   'column',
          fontFamily:      "'Inter', system-ui, sans-serif",
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        {/* Header */}
        <div style={{
          padding:       '16px 16px 14px',
          borderBottom:  '1px solid rgba(255,255,255,0.05)',
          display:       'flex',
          alignItems:    'center',
          justifyContent:'space-between',
          flexShrink:    0,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <LogoMark size={36} />
            <div>
              <div style={{ display:'flex', alignItems:'baseline', gap:'2px' }}>
                <span style={{ fontSize:'14px', fontWeight:300, letterSpacing:'0.1em',
                  color:'#ffffff' }}>ATMOSPHERE</span>
                <span style={{ fontSize:'14px', fontWeight:700, letterSpacing:'0.04em',
                  color:'#00e5cc' }}>8K</span>
              </div>
              <div style={{ fontSize:'9px', color:'rgba(255,255,255,0.25)',
                letterSpacing:'0.12em', textTransform:'uppercase', marginTop:'2px' }}>
                Live Wind Visualization
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close controls"
            style={{
              width:'32px', height:'32px', borderRadius:'8px',
              background:'rgba(255,255,255,0.06)',
              border:'1px solid rgba(255,255,255,0.08)',
              color:'rgba(255,255,255,0.5)',
              cursor:'pointer', fontSize:'16px',
              display:'flex', alignItems:'center', justifyContent:'center',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY:'auto', flex:1, padding:'16px 16px 32px' }}>

          {/* Wind toggle */}
          <div style={{ marginTop:'4px' }}>
            <div
              onClick={() => upd('showWind', !s.showWind)}
              style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                background: s.showWind ? 'rgba(0,229,204,0.06)' : 'rgba(255,255,255,0.03)',
                border: s.showWind ? '1px solid rgba(0,229,204,0.2)' : '1px solid rgba(255,255,255,0.07)',
                borderRadius:'10px', padding:'10px 14px', cursor:'pointer',
              }}
            >
              <div>
                <div style={{ fontSize:'12px', fontWeight:500,
                  color: s.showWind ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)' }}>
                  Wind Layer
                </div>
                <div style={{ fontSize:'9px', marginTop:'2px',
                  color: s.showWind ? '#00e5cc' : 'rgba(255,255,255,0.2)',
                  letterSpacing:'0.06em' }}>
                  {s.showWind ? 'Visible' : 'Hidden'}
                </div>
              </div>
              <div style={{
                width:'38px', height:'22px', borderRadius:'11px',
                background: s.showWind ? '#00e5cc' : 'rgba(255,255,255,0.1)',
                position:'relative', transition:'background 0.2s',
              }}>
                <div style={{
                  position:'absolute', top:'3px',
                  left: s.showWind ? '19px' : '3px',
                  width:'16px', height:'16px', borderRadius:'50%',
                  background:'#fff', transition:'left 0.2s',
                  boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
                }}/>
              </div>
            </div>
          </div>

          <Section title="Flow" />
          <Slider label="Speed" value={s.timeScale} min={400} max={5000} step={100}
            display={s.timeScale<800?'Slow':s.timeScale<2200?'Normal':s.timeScale<3800?'Fast':'Storm'}
            onChange={(v:number) => upd('timeScale',v)}/>
          <Slider label="Density" value={s.particleCount} min={5000} max={80000} step={2500}
            display={Math.round(s.particleCount/1000)+'k'}
            onChange={(v:number) => upd('particleCount',v)}/>
          <Slider label="Trail Length" value={s.trailFade} min={0.005} max={0.08} step={0.005}
            display={trailLabel}
            onChange={(v:number) => upd('trailFade',v)}/>
          <Slider label="Trail Width" value={s.trailWidth} min={0.3} max={2.5} step={0.1}
            display={s.trailWidth.toFixed(1)}
            onChange={(v:number) => upd('trailWidth',v)}/>

          <Section title="Appearance" />
          <Slider label="Particle Opacity" value={s.particleOpacity} min={0.2} max={1.0} step={0.05}
            display={Math.round(s.particleOpacity*100)+'%'} accent="#7eb8ff"
            onChange={(v:number) => upd('particleOpacity',v)}/>
          <Slider label="Globe Brightness" value={s.globeBrightness} min={0.2} max={1.0} step={0.05}
            display={Math.round(s.globeBrightness*100)+'%'} accent="#7eb8ff"
            onChange={(v:number) => upd('globeBrightness',v)}/>

          {/* Reverse flow */}
          <div
            onClick={() => upd('reverseFlow', !s.reverseFlow)}
            style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
              background: s.reverseFlow
                ? 'rgba(0,229,204,0.06)' : 'rgba(255,255,255,0.03)',
              border: s.reverseFlow
                ? '1px solid rgba(0,229,204,0.2)'
                : '1px solid rgba(255,255,255,0.06)',
              borderRadius: '10px',
              padding: '9px 12px',
              cursor: 'pointer',
              marginTop: '8px',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: 500,
                color: s.reverseFlow
                  ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)' }}>
                Reverse Flow
              </div>
              <div style={{ fontSize: '9px', marginTop: '2px',
                color: s.reverseFlow ? '#00e5cc' : 'rgba(255,255,255,0.2)',
                letterSpacing: '0.06em' }}>
                {s.reverseFlow ? 'Running backwards' : 'Normal direction'}
              </div>
            </div>
            <div style={{
              width: '38px', height: '22px', borderRadius: '11px',
              background: s.reverseFlow ? '#00e5cc' : 'rgba(255,255,255,0.1)',
              position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{
                position: 'absolute', top: '3px',
                left: s.reverseFlow ? '19px' : '3px',
                width: '16px', height: '16px', borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
              }}/>
            </div>
          </div>

          <Section title="Atmosphere Glow" />
          <Slider
            label="Glow Colour"
            value={s.glowHue}
            min={0} max={360} step={5}
            display={
              s.glowHue === 0   ? 'Blue'    :
              s.glowHue <= 30   ? 'Azure'   :
              s.glowHue <= 70   ? 'Cyan'    :
              s.glowHue <= 150  ? 'Green'   :
              s.glowHue <= 195  ? 'Teal'    :
              s.glowHue <= 260  ? 'Purple'  :
              s.glowHue <= 310  ? 'Magenta' :
              s.glowHue <= 345  ? 'Red'     : 'Blue'
            }
            accent="#c084fc"
            onChange={(v: number) => upd('glowHue', v)}
          />
          <Slider
            label="Glow Intensity"
            value={s.glowOpacity}
            min={0} max={1.0} step={0.05}
            display={
              s.glowOpacity === 0   ? 'Off' :
              s.glowOpacity <= 0.25 ? 'Faint' :
              s.glowOpacity <= 0.6  ? 'Soft'  :
              s.glowOpacity <= 0.85 ? 'Normal': 'Max'
            }
            accent="#c084fc"
            onChange={(v: number) => upd('glowOpacity', v)}
          />

          <Section title="Animation Mode" />
          <div style={{ display: 'flex', gap: '6px' }}>
            {([
              { id: 'wind',     label: 'Wind',     sub: 'Live GFS'   },
              { id: 'straight', label: 'Linear',   sub: 'Flow lines' },
              { id: 'random',   label: 'Random',   sub: 'Free flow'  },
            ] as const).map(m => (
              <button
                key={m.id}
                onClick={() => upd('animMode', m.id)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: s.animMode === m.id
                    ? 'rgba(0,229,204,0.1)' : 'rgba(255,255,255,0.03)',
                  border: s.animMode === m.id
                    ? '1px solid rgba(0,229,204,0.3)'
                    : '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '10px', fontWeight: 600,
                  color: s.animMode === m.id
                    ? '#00e5cc' : 'rgba(255,255,255,0.4)',
                  letterSpacing: '0.04em' }}>
                  {m.label}
                </div>
                <div style={{ fontSize: '9px',
                  color: 'rgba(255,255,255,0.2)', marginTop: '2px' }}>
                  {m.sub}
                </div>
              </button>
            ))}
          </div>

          <Section title="Background" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {([
              { id: 'stars',     label: 'Stars',     sub: 'Default'      },
              { id: 'milkyway',  label: 'Milky Way', sub: 'Deep field'   },
              { id: 'heart',     label: 'Heart',     sub: 'Nebula'       },
              { id: 'heartsoul', label: 'H & Soul',  sub: 'Nebula pair'  },
            ] as const).map(bg => (
              <button
                key={bg.id}
                onClick={() => upd('bgScene', bg.id)}
                style={{
                  padding: '7px 8px',
                  background: s.bgScene === bg.id
                    ? 'rgba(0,229,204,0.08)' : 'rgba(255,255,255,0.03)',
                  border: s.bgScene === bg.id
                    ? '1px solid rgba(0,229,204,0.3)'
                    : '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 500,
                  color: s.bgScene === bg.id
                    ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)',
                }}>
                  {bg.label}
                </div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.2)',
                  marginTop: '2px' }}>
                  {bg.sub}
                </div>
              </button>
            ))}
          </div>

          <Section title="Colour Theme" />
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {THEMES.map(t => (
              <button key={t.id}
                onClick={() => upd('colorTheme', t.id as ColorTheme)}
                style={{
                  display:'flex', alignItems:'center', gap:'10px',
                  padding:'9px 11px',
                  background: s.colorTheme===t.id ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                  border: s.colorTheme===t.id ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.06)',
                  borderRadius:'8px', cursor:'pointer', width:'100%', textAlign:'left',
                }}>
                <div style={{ width:'44px', height:'8px', borderRadius:'4px', flexShrink:0,
                  background:'linear-gradient(to right,'+t.preview.join(',')+')'}}/>
                <span style={{ fontSize:'11px', fontWeight:500,
                  color: s.colorTheme===t.id ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)' }}>
                  {t.label}
                </span>
                {s.colorTheme===t.id && (
                  <span style={{ marginLeft:'auto', fontSize:'10px', color:'#00e5cc' }}>✓</span>
                )}
              </button>
            ))}
          </div>

          <Section title="Surface" />
          <div style={{ display: 'flex', flexDirection: 'column' }}>

            {SURFACE_CATEGORIES.map(cat => (
              <div key={cat.id}>
                {/* Category label */}
                <div style={{
                  fontSize: '9px', fontWeight: 600,
                  letterSpacing: '0.22em',
                  color: 'rgba(255,255,255,0.22)',
                  textTransform: 'uppercase',
                  margin: '12px 0 6px',
                }}>
                  {cat.label}
                </div>

                {/* Planet grid — 3 columns for Earth, 2 for rest */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: cat.planets.length === 3
                    ? '1fr 1fr 1fr' : '1fr 1fr',
                  gap: '5px',
                }}>
                  {cat.planets.map(p => (
                    <button
                      key={p.id}
                      onClick={() => {
                        const theme = PLANET_THEMES[p.id];
                        const next  = { ...s, planetSurface: p.id } as GlobeSettings;
                        if (theme) next.colorTheme = theme;
                        setS(next);
                        onChange({ planetSurface: p.id });
                        if (theme) onChange({ colorTheme: theme });
                      }}
                      style={{
                        display:     'flex',
                        alignItems:  'center',
                        gap:         '6px',
                        padding:     '6px 8px',
                        background:  s.planetSurface === p.id
                          ? 'rgba(0,229,204,0.08)' : 'rgba(255,255,255,0.03)',
                        border: s.planetSurface === p.id
                          ? '1px solid rgba(0,229,204,0.35)'
                          : '1px solid rgba(255,255,255,0.06)',
                        borderRadius: '6px',
                        cursor:       'pointer',
                        minWidth:     0,
                      }}
                    >
                      <div style={{
                        width:        '8px',
                        height:       '8px',
                        borderRadius: '50%',
                        background:   p.dot,
                        flexShrink:   0,
                        boxShadow:    s.planetSurface === p.id
                          ? '0 0 5px ' + p.dot + '99' : 'none',
                      }}/>
                      <span style={{
                        fontSize:     '11px',
                        fontWeight:   500,
                        color:        s.planetSurface === p.id
                          ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)',
                        whiteSpace:   'nowrap',
                        overflow:     'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {p.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* User Pro — placeholder */}
            <div>
              <div style={{
                fontSize: '9px', fontWeight: 600,
                letterSpacing: '0.22em',
                color: 'rgba(255,255,255,0.22)',
                textTransform: 'uppercase',
                margin: '12px 0 6px',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                User
                <span style={{
                  fontSize: '8px', fontWeight: 700,
                  letterSpacing: '0.08em',
                  background: 'rgba(0,229,204,0.1)',
                  color: '#00e5cc',
                  padding: '2px 6px',
                  borderRadius: '3px',
                }}>
                  PRO
                </span>
              </div>
              <div style={{
                display:      'flex',
                alignItems:   'center',
                gap:          '8px',
                padding:      '8px 10px',
                border:       '1px dashed rgba(255,255,255,0.07)',
                borderRadius: '6px',
              }}>
                <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.15)' }}>
                  ⬡
                </span>
                <span style={{
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.2)',
                  letterSpacing: '0.03em',
                }}>
                  Custom surfaces coming soon
                </span>
              </div>
            </div>

          </div>

          {/* Quality toggle */}
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            marginTop:'16px', paddingTop:'14px',
            borderTop:'1px solid rgba(255,255,255,0.05)',
          }}>
            <span style={{ fontSize:'9px', fontWeight:600, letterSpacing:'0.22em',
              color:'rgba(255,255,255,0.25)', textTransform:'uppercase' }}>
              Quality
            </span>
            <button
              onClick={() => upd('textureQuality', s.textureQuality==='8k'?'2k':'8k')}
              style={{
                display:'flex', alignItems:'center', gap:'5px',
                padding:'5px 12px', fontSize:'10px', fontWeight:600, letterSpacing:'0.08em',
                background: s.textureQuality==='8k' ? 'rgba(0,229,204,0.1)' : 'rgba(255,255,255,0.04)',
                border: s.textureQuality==='8k' ? '1px solid rgba(0,229,204,0.35)' : '1px solid rgba(255,255,255,0.08)',
                borderRadius:'6px',
                color: s.textureQuality==='8k' ? '#00e5cc' : 'rgba(255,255,255,0.3)',
                cursor:'pointer',
              }}>
              8K {s.textureQuality==='8k' && '✓'}
            </button>
            <span style={{ fontSize:'9px', color:'rgba(255,255,255,0.2)' }}>
              {s.textureQuality==='8k' ? 'Ultra' : 'Standard'}
            </span>
          </div>

          {/* Footer */}
          <div style={{
            marginTop:'24px', paddingTop:'16px',
            borderTop:'1px solid rgba(255,255,255,0.04)',
            textAlign:'center',
          }}>
            <div style={{ fontSize:'9px', color:'rgba(255,255,255,0.15)',
              letterSpacing:'0.06em', marginBottom:'6px' }}>
              NOAA GFS · Live Wind Data
            </div>
            <div style={{ fontSize:'9px', color:'rgba(255,255,255,0.15)',
              letterSpacing:'0.06em', marginBottom:'6px' }}>
              Atmosphere8K Build Prompt
            </div>
            
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 480px) {
          [role="dialog"] { width: 100vw !important; border-right: none !important; }
        }
      `}</style>
    </>
  );
}
